import { Injectable }       from '@angular/core';
import { HttpClient }       from '@angular/common/http';
import { catchError, forkJoin, map, Observable, of }       from 'rxjs';
import { ConsultationPublique, ConsultationPubliqueMessageBaniere } from '../models/consultation-publique.models';
import { ZzzConfigService } from 'src/app/zzz/core/config/config.service';
import { DomainesService } from 'src/app/shared/services/domaines.service';
import { ValeurDomaineDto } from '../models/domaines.dto';
import { ReponseActionApi, ReponseActionApiBase, ActionApiMessage } from 'src/app/zzz/core/shared/core.models';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class ConsultationsPubliqueService {

  private baseUrl: string;
  private apiUrl: string;

  constructor(
    private http: HttpClient,
    private configService: ZzzConfigService,
    private domainesService: DomainesService,
    private translate: TranslateService
  ) {
    this.baseUrl = this.configService.get().authApiEndPoint;
    this.apiUrl = `${this.baseUrl}/sigtapi/pilotage/consultation-publique`;
  }

  getAll(activeSeulement:boolean = false): Observable<{valeur: ConsultationPublique[]}> {
    return this.http.get<{valeur: ConsultationPublique[]}>(this.apiUrl, { params: { ActiveSeulement: false } }).pipe(
      map(response => ({
        ...response,
        valeur: response.valeur.map(item => ({
          ...item,
          dateDebut: new Date(item.dateDebut),
          dateFin: new Date(item.dateFin)
        }))
      })));
  }

  getActives(): Observable<{valeur: ConsultationPublique[]}> {
      return this.http.get<{valeur: ConsultationPublique[]}>(this.apiUrl, { params: { ActiveSeulement: true } }).pipe(
      map(response => ({
        ...response,
        valeur: response.valeur.map(item => ({
          ...item,
          dateDebut: new Date(item.dateDebut),
          dateFin: new Date(item.dateFin)
        }))
      })));
  }

  add(entry: ConsultationPublique): Observable<{valeur:ConsultationPublique}> {
    return this.http.post<{valeur:ConsultationPublique}>(this.apiUrl, {valeur:entry});
  }

  update(entry: ConsultationPublique): Observable<{valeur:ConsultationPublique}> {
    return this.http.put<{valeur:ConsultationPublique}>(this.apiUrl, {valeur:entry});
  }

  delete(ids: number[]): Observable<ReponseActionApiBase> {
    return this.http.delete<ReponseActionApiBase>(`${this.apiUrl}`, { body: { ids } });
  }

  getMessageBaniere(): Observable<ConsultationPubliqueMessageBaniere | null> {
    return  forkJoin({
      consultationsActives: this.getActives().pipe(catchError(() => of({ valeur: [] }))),
      domaineRegions: this.domainesService.obtenirValeurs('REGION_ADMIN').pipe(catchError(() => of({ valeursDomaine: [] }))),
    }).pipe(
      map(({ consultationsActives, domaineRegions }) => {
          return this.creerMessageBaniere(consultationsActives?.valeur ?? [], domaineRegions?.valeursDomaine ?? []);
      })
    );
  }

  private creerMessageBaniere(
    consultationsActives: ConsultationPublique[],
    domaineRegions: ValeurDomaineDto[]
  ): ConsultationPubliqueMessageBaniere | null {
    const consultationsPublique = consultationsActives;
    if (consultationsPublique.length === 0) {
      return null;
    }

    let baniere: ConsultationPubliqueMessageBaniere = { titre: '', message: '' };

    if (consultationsPublique.length === 1) {

      const consultation = consultationsPublique[0];
      const regions = consultation.regions.map(region => domaineRegions.find(d => d.valeur === region)?.description).join(", ");

      baniere.titre = this.translate.instant('consultationPublique.baniere.uneConsultation.titre')
        .replace('{0}', regions);

      baniere.message = this.translate.instant('consultationPublique.baniere.uneConsultation.messageIntro')
        .replace('{0}', consultation.nom)
        .replace('{1}', consultation.dateFin.toLocaleDateString('fr-CA', { day: "numeric", month: "long", year: "numeric" }));

      if (consultation.regions.length === 1) {
        baniere.message += this.translate.instant('consultationPublique.baniere.uneConsultation.messageUneRegion');
      } else {
        baniere.message += this.translate.instant('consultationPublique.baniere.uneConsultation.messagePlusieursRegions');
      }

    } else {

      baniere.titre = this.translate.instant('consultationPublique.baniere.plusieursConsultations.titre');

      for (const consultation of consultationsPublique) {

        const regions = consultation.regions.map(region => domaineRegions.find(d => d.valeur === region)?.description).join(", ");

        baniere.message += this.translate.instant('consultationPublique.baniere.plusieursConsultations.ligneConsultation')
          .replace('{0}', consultation.nom)
          .replace('{1}', consultation.dateFin.toLocaleDateString('fr-CA', { day: "numeric", month: "long", year: "numeric" }))
          .replace('{2}', regions);
      }

      baniere.message += this.translate.instant('consultationPublique.baniere.plusieursConsultations.conclusion');
    }

    const prefix = this.translate.instant('consultationPublique.baniere.action.soumettreCommentairePrefix');
    const texteLien = this.translate.instant('consultationPublique.baniere.action.soumettreCommentaireTexteLien');
    const suffix = this.translate.instant('consultationPublique.baniere.action.soumettreCommentaireSuffix');

    baniere.message += `${prefix} « <a href class="open-comment-tool">${texteLien}</a> » ${suffix}`;

    return baniere;
  }


}
